import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { SessionStorageService, SessionStorage } from 'ngx-webstorage';
import { addDays, isSameDay, isWeekend, startOfDay } from 'date-fns';

import { ConfigService } from 'services/config/config.service';

import { CategoryPipe } from 'pipes/category.pipe';
import { ReferralFacilitatorStatusPipe } from 'pipes/referralfacilitatorstatus.pipe';
import { ReferralStatusPipe } from 'pipes/referralstatus.pipe';
import { RequestIssuePipe } from 'pipes/requestissue.pipe';
import { RequestStatusPipe } from 'pipes/requeststatus.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';
import { FileUploadInformation } from 'modules/fileupload/fileupload.service';

@Injectable({
    providedIn: 'root',
})
export class ApplicationService {
    DevSiteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    ThemeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    CurrentRequestSubject: BehaviorSubject<CurrentRequestInfo> = new BehaviorSubject<CurrentRequestInfo>(null);

    // #region Getters and Setters

    public get DevSite(): boolean {
        return this.DevSiteSubject.value;
    }

    public set DevSite(value: boolean) {
        this.DevSiteSubject.next(value);
    }

    public get CurrentRequest(): CurrentRequestInfo {
        return this.CurrentRequestSubject.value;
    }

    public set CurrentRequest(value: CurrentRequestInfo) {
        this.CurrentRequestSubject.next(value);
        this._CurrentRequest = value;
    }

    // #endregion

    @SessionStorage() _AllBoardMembers: PersonInfo[];
    @SessionStorage() _AllCabinetMembers: PersonInfo[];
    @SessionStorage() _AllCategories: CategoryInfo[];
    @SessionStorage() _AllConstituents: string[];
    @SessionStorage() _AllEmailPreferenceOptions: EmailPreferenceOption[];
    @SessionStorage() _AllReferredToUsers: PersonInfo[];
    @SessionStorage() _AllRoles: RoleInfo[];
    @SessionStorage() _AllSchoolYears: string[];
    @SessionStorage() _AllSubordinates: PersonInfo[];
    @SessionStorage() _AllSubordinatesSupervisorSAPID: string;
    @SessionStorage() _AllSupervisors: PersonInfo[];
    @SessionStorage() _CurrentSchoolYear: string;
    @SessionStorage() _LoadedSchoolYear: string;
    @SessionStorage() _AllSchools: SchoolInfo[];
    @SessionStorage() _CurrentRequest: CurrentRequestInfo;
    @SessionStorage() _WorkdayCalendars: WorkdayCalendarInfo[];

    constructor(
        private http: HttpClient,
        private categoryPipe: CategoryPipe,
        private configService: ConfigService,
        private storage: SessionStorageService,
        private referralFacilitatorStatusPipe: ReferralFacilitatorStatusPipe,
        private referralStatusPipe: ReferralStatusPipe,
        private requestIssuePipe: RequestIssuePipe,
        private requestStatusPipe: RequestStatusPipe,
        private titleCasePipe: TitleCasePipe
    ) {
        this.DevSite = window.location.hostname.indexOf('localhost') >= 0 || window.location.hostname.indexOf('polkawardsdev') >= 0;

        // Anything with dates must be reparsed in case this is a page refresh retrieving from session variables
        this.CurrentRequestSubject.next(this._CurrentRequest);

        (this._WorkdayCalendars || []).forEach(c => {
            c.CalendarDate = c.CalendarDate ? new Date(c.CalendarDate) : null;
        });
    }

    // #region Add Board Assistant Service Call

    AddBoardAssistant(AuthToken: string, CurrentRole: string, SAPID: string, PositionNo: string) {
        return this.http.get<PersonInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/AddBoardAssistant` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}&PositionNo=${PositionNo || ''}`
        );
    }

    // #endregion

    // #region Add Board Note To Request Service Call

    AddBoardNoteToRequest(AuthToken: string, CurrentRole: string, RequestID: number, Note: string) {
        return this.http.post<RequestCommentInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/AddBoardNoteToRequest`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            RequestID: RequestID != null ? RequestID : '',
            Note: Note || '',
        });
    }

    // #endregion

    // #region Add Cabinet Member Service Call

    AddCabinetMember(AuthToken: string, CurrentRole: string, SAPID: string, PositionNo: string, CabinetType: string) {
        return this.http
            .get<PersonInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/AddCabinetMember` +
                    `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}` +
                    `&PositionNo=${PositionNo || ''}&CabinetType=${CabinetType || ''}`
            )
            .pipe(
                map(cabinetmembers => {
                    this._AllCabinetMembers = cabinetmembers || [];

                    return this._AllCabinetMembers;
                })
            );
    }

    // #endregion

    // #region Add Facilitator Service Call

    AddFacilitator(AuthToken: string, CurrentRole: string, SAPID: string, PositionNo: string) {
        return this.http.get<PersonInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/AddFacilitator` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}&PositionNo=${PositionNo || ''}`
        );
    }

    // #endregion

    // #region Add FollowUp Response Service Call

    AddFollowUpResponse(AuthToken: string, CurrentRole: string, RequestID: number, FollowUpID: number, Note: string) {
        return this.http.post<boolean>(this.configService.hostAddress + `Api/Services/IRIS/AddFollowUpResponse`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            RequestID: RequestID != null ? RequestID : '',
            FollowUpID: FollowUpID != null ? FollowUpID : '',
            Note: Note || '',
        });
    }

    // #endregion

    // #region Add FollowUp To Request Service Call

    AddFollowUpToRequest(AuthToken: string, CurrentRole: string, RequestID: number, Note: string) {
        return this.http.post<boolean>(this.configService.hostAddress + `Api/Services/IRIS/AddFollowUpToRequest`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            RequestID: RequestID != null ? RequestID : '',
            Note: Note || '',
        });
    }

    // #endregion

    // #region Add Referral Service Call

    AddReferral(AuthToken: string, RequestDetails: CreateOptions) {
        return this.http.post<AddRequest>(this.configService.hostAddress + `Api/Services/IRIS/AddReferral`, {
            AuthToken,
            infoObject: {
                RequestID: RequestDetails.RequestID,
                RequestedBySAPID: RequestDetails.RequestedBySAPID,
                SchoolNumber: RequestDetails.SchoolNumber,
                CategoryID: RequestDetails.RequestCategory.CategoryID,
                BoardDistrict: RequestDetails.BoardDistrict,
                Priority: RequestDetails.Priority,
                DueDate: RequestDetails.DueDate,
                SourceNotes: RequestDetails.SourceNotes,
            },
        });
    }

    // #endregion

    // #region Add Request Service Call

    AddRequest(AuthToken: string, RequestDetails: CreateOptions) {
        return this.http
            .post<AddRequest>(this.configService.hostAddress + `Api/Services/IRIS/AddRequest`, {
                AuthToken,
                infoObject: {
                    RequestText: RequestDetails.RequestText || '',
                    Source: RequestDetails.Source || '',
                    SourceDescription: RequestDetails.SourceDescription || '',
                    RequestedBySAPID: RequestDetails.RequestedBySAPID || '',
                    Subject: RequestDetails.Subject || '',
                    ConstituentName: RequestDetails.ConstituentName || '',
                    ConstituentEmail: RequestDetails.ConstituentEmail || '',
                    ConstituentPhone: RequestDetails.ConstituentPhone || '',
                    ConstituentPrivate: RequestDetails.ConstituentPrivate != null ? RequestDetails.ConstituentPrivate : false,
                    SchoolNumber: RequestDetails.SchoolNumber || '',
                    CategoryID: RequestDetails.RequestCategory.CategoryID || '',
                    BoardDistrict: RequestDetails.BoardDistrict || '',
                    Priority: RequestDetails.Priority || '',
                    Confidential: RequestDetails.Confidential != null ? RequestDetails.Confidential : '',
                    DueDate: RequestDetails.DueDate || '',
                    SourceNotes: RequestDetails.SourceNotes || '',
                },
            })
            .pipe(
                map(data => {
                    if (data) {
                        data.RequestID = data.RequestID;
                        data.RequestIssueNumber = data.RequestIssueNumber;
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Assign Request Service Call

    AssignRequest(AuthToken: string, CurrentRole: string, RequestDetails: CreateOptions) {
        return this.http.post<AddRequest>(this.configService.hostAddress + `Api/Services/IRIS/AssignRequest`, {
            AuthToken,
            CurrentRole,
            infoObject: {
                RequestID: RequestDetails.RequestID,
                RequestedBySAPID: RequestDetails.RequestedBySAPID,
                SchoolNumber: RequestDetails.SchoolNumber,
                CategoryID: RequestDetails.RequestCategory.CategoryID,
                BoardDistrict: RequestDetails.BoardDistrict,
                Priority: RequestDetails.Priority,
                DueDate: RequestDetails.DueDate,
                SourceNotes: RequestDetails.SourceNotes,
                Confidential: RequestDetails.Confidential != null ? RequestDetails.Confidential : false,
            },
        });
    }

    // #endregion

    // #region Board Approve Referral Service Call

    BoardApproveReferral(AuthToken: string, RequestID: number, ReferrerNotes?: string) {
        return this.http.post<number>(this.configService.hostAddress + `Api/Services/IRIS/BoardApproveReferral`, {
            AuthToken,
            RequestID,
            ReferrerNotes,
        });
    }

    // #endregion

    // #region Close As Non Info Request Service Call

    CloseAsNonInfoRequest(AuthToken: string, CurrentRole: string, RequestID: number) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
                `Api/Services/IRIS/CloseAsNonInfoRequest?AuthToken=${AuthToken || ''}&` +
                `CurrentRole=${CurrentRole || ''}&RequestID=${RequestID || ''}`
        );
    }

    // #endregion

    // #region Deactivate Out Of Office Setting Service Call

    DeactivateOutOfOfficeSetting(AuthToken: string, CurrentRole: string, OutOfOfficeSettingID: number, ToRemove: boolean) {
        return this.http
            .get<OutOfOfficeSetting[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/DeactivateOutOfOfficeSetting?AuthToken=${AuthToken || ''}` +
                    `&CurrentRole=${CurrentRole || ''}&OutOfOfficeSettingID=${OutOfOfficeSettingID || ''}` +
                    `&ToRemove=${ToRemove != null ? ToRemove : ''}`
            )
            .pipe(
                map(data => {
                    if (data && data.length) {
                        data.forEach(d => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Delete File Service Call

    DeleteFile(AuthToken: string, IRISAttachmentID: number, FilePath: string) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
                `Api/Services/IRIS/DeleteFile?AuthToken=${AuthToken || ''}&IRISAttachmentID=${IRISAttachmentID || ''}` +
                `&FilePath=${FilePath || ''}`
        );
    }

    // #endregion

    // #region Format Request Info

    FormatRequestInfo(request: RequestInfo, CurrentRole: string) {
        const self = this;

        if (!request) {
            return;
        }

        request.FormattedRequestIssueNumber = self.requestIssuePipe.transform(request.RequestIssueNumber, request.FiscalYear);

        // For priority, first save the original priority as it is coming in. Then, if the current request status is NEEDTIME (indicating
        // that we are holding for a board time request), we should virtually reset the priority to -1 (pending approval) so the UI indicates
        // this.
        request.OriginalPriority = request.Priority;

        if (request.Status === 'NEEDTIME') {
            request.Priority = -1;
        }

        request.PriorityShortLabel =
            request.Priority === -1
                ? 'Waiting for Approval'
                : request.Priority === 1
                ? 'Urgent'
                : request.Priority === 2
                ? 'High'
                : request.Priority === 3
                ? 'Routine'
                : request.Priority === 4
                ? 'Low'
                : 'Unassigned';
        request.PriorityLabel =
            request.Priority === -1 ? 'Waiting for Approval' : request.Priority + ' - ' + request.PriorityShortLabel;
        request.CategoryLabel = self.categoryPipe.transform(request.CategoryID, self._AllCategories);
        request.DateClosed = request.DateClosed ? new Date(request.DateClosed) : null;
        request.DateCreated = request.DateCreated ? new Date(request.DateCreated) : null;
        request.DateReferred = request.DateReferred ? new Date(request.DateReferred) : null;
        request.DateUpdated = request.DateUpdated ? new Date(request.DateUpdated) : null;
        request.DueDate = request.DueDate ? new Date(request.DueDate) : null;
        request.ReferralDateUpdated = request.ReferralDateUpdated ? new Date(request.ReferralDateUpdated) : null;
        request.SchoolNumber = request.SchoolNumber == null ? '' : request.SchoolNumber.replace(/\s/g, '');
        request.BoardDistrict = request.BoardDistrict == null ? '' : request.BoardDistrict.replace(/\s/g, '');
        switch (request.Status) {
            case 'DENIED':
            case 'UNAVAIL':
            case 'COMPLETED':
            case 'CANCEL':
            case 'CLOSEDNONINFO':
                request.RequestIsClosed = true;
                break;
            default:
                request.RequestIsClosed = false;
                break;
        }
        // Normalize the capitalization of the referred to person's name, the requestor's name, and the referrer's name (for any provided)
        if (request.RequestorInfo) {
            request.RequestorInfo.LastName = self.titleCasePipe.transform(request.RequestorInfo.LastName);
            request.RequestorInfo.FirstName = self.titleCasePipe.transform(request.RequestorInfo.FirstName);
            request.RequestorInfo.FullName =
                !!request.RequestorInfo.LastName && !!request.RequestorInfo.FirstName
                    ? request.RequestorInfo.LastName + ', ' + request.RequestorInfo.FirstName
                    : null;
            request.RequestorName = request.RequestorInfo.FullName;
        }

        if (request.ReferrerInfo) {
            request.ReferrerInfo.LastName = self.titleCasePipe.transform(request.ReferrerInfo.LastName);
            request.ReferrerInfo.FirstName = self.titleCasePipe.transform(request.ReferrerInfo.FirstName);
            request.ReferrerInfo.FullName =
                !!request.ReferrerInfo.LastName && !!request.ReferrerInfo.FirstName
                    ? request.ReferrerInfo.LastName + ', ' + request.ReferrerInfo.FirstName
                    : null;
        }

        if (request.ResponderInfo) {
            request.ResponderInfo.LastName = self.titleCasePipe.transform(request.ResponderInfo.LastName);
            request.ResponderInfo.FirstName = self.titleCasePipe.transform(request.ResponderInfo.FirstName);
            request.ResponderInfo.FullName =
                !!request.ResponderInfo.LastName && !!request.ResponderInfo.FirstName
                    ? request.ResponderInfo.LastName + ', ' + request.ResponderInfo.FirstName
                    : null;
            request.ResponderName = request.ResponderInfo.FullName;
        }

        if (request.SecondResponderInfo) {
            request.SecondResponderInfo.LastName = self.titleCasePipe.transform(request.SecondResponderInfo.LastName);
            request.SecondResponderInfo.FirstName = self.titleCasePipe.transform(request.SecondResponderInfo.FirstName);
            request.SecondResponderInfo.FullName =
                !!request.SecondResponderInfo.LastName && !!request.SecondResponderInfo.FirstName
                    ? request.SecondResponderInfo.LastName + ', ' + request.SecondResponderInfo.FirstName
                    : null;
        }

        request.ReferralIsClosed = ['DONEWINFO', 'DONENOINFO', 'CANCEL', 'DENIED', 'UNAVAIL', 'COMPLETED', 'CLOSEDNONINFO'].includes(
            request.Status
        );
        request.StatusLabel = self.referralStatusPipe.transform(
            request.Status,
            CurrentRole === 'BoardMember' || CurrentRole === 'BoardAssistant',
            request.Completed
        );
        request.ReferralFacilitatorStatus = self.referralFacilitatorStatusPipe.transform(request.Status);

        if (request.Comments && !!request.Comments.length) {
            request.Comments.forEach(c => {
                c.DateCommentAdded = c.DateCommentAdded ? new Date(c.DateCommentAdded) : null;
                c.HasRelatedLink = /Request #\d{4}-\d{3,}/.test(c.Comment);

                if (c.HasRelatedLink) {
                    const linkparts = c.Comment.match(/Request #(\d{4})-(\d{3,})/);

                    if (linkparts.length >= 3) {
                        c.RelatedRequestLinkYear = linkparts[1];
                        c.RelatedRequestLinkNumber = linkparts[2];
                    }
                }
            });
        }

        if (request.FollowUps && request.FollowUps.length) {
            const fatt = [] as FileAttachmentInfo[];

            request.FollowUps.forEach(f => {
                if (f.Attachments && f.Attachments.length) {
                    f.Attachments.forEach(t => fatt.push(t));
                }
            });

            request.FollowUpAttachments = fatt;
        }
    }

    // #endregion

    // #region Get All Board Assistants Service Call

    GetAllBoardAssistants() {
        return this.http.get<PersonInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/GetAllBoardAssistants`);
    }

    // #endregion

    // #region Get All Board Members Service Call

    GetAllBoardMembers() {
        const self = this;

        return self._AllBoardMembers && !!self._AllBoardMembers.length
            ? of<PersonInfo[]>(self._AllBoardMembers)
            : self.http.get<PersonInfo[]>(self.configService.hostAddress + `Api/Services/IRIS/GetAllBoardMembers`).pipe(
                  map(data => {
                      self._AllBoardMembers = data || [];

                      return self._AllBoardMembers;
                  })
              );
    }

    // #endregion

    // #region Get All Cabinet Members Service Call

    GetAllCabinetMembers() {
        const self = this;

        return self.http.get<PersonInfo[]>(self.configService.hostAddress + `Api/Services/IRIS/GetAllCabinetMembers`).pipe(
            map(data => {
                self._AllCabinetMembers = data || [];

                return self._AllCabinetMembers;
            })
        );
    }

    // #endregion

    // #region Get All Referrals For SAPID Service Call

    GetAllReferralsForSAPID(AuthToken: string, CurrentRole: string, SAPID: string) {
        const self = this;

        return this.http
            .get<RequestInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/GetAllReferralsForSAPID` +
                    `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}`
            )
            .pipe(
                map(results => {
                    if (results && !!results.length) {
                        results.forEach(result => {
                            self.FormatRequestInfo(result, CurrentRole);
                        });
                    }

                    return results;
                })
            );
    }

    // #endregion

    // #region Get All Categories Service Call

    GetAllCategories() {
        const self = this;

        return self.http.get<CategoryInfo[]>(self.configService.hostAddress + 'Api/Services/IRIS/GetAllCategories').pipe(
            map(cats => {
                self._AllCategories = cats;

                return cats;
            })
        );
    }

    // #endregion

    // #region Get All Constituents Service Call

    GetAllConstituents(AuthToken: string) {
        const self = this;

        return self._AllConstituents && !!self._AllConstituents.length
            ? of<string[]>(self._AllConstituents).pipe(take(1))
            : this.http.get<string[]>(
                  this.configService.hostAddress + `Api/Services/IRIS/GetAllRequestConstituents?AuthToken=${AuthToken || ''}`
              );
    }

    // #endregion

    // #region Get All Facilitators Service Call

    GetAllFacilitators() {
        return this.http.get<PersonInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/GetAllFacilitators`);
    }

    // #endregion

    // #region Get All Referred To Users Service Call

    GetAllReferredToUsers(AuthToken: string) {
        return this.http
            .get<PersonInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/GetAllReferredToUsers?AuthToken=${AuthToken || ''}`)
            .pipe(
                map(data => {
                    return data;
                })
            );
    }

    // #endregion

    // #region Get All Schools Service Call

    GetAllSchools() {
        const self = this;

        if (self._AllSchools && !!self._AllSchools.length) {
            // We already have the list of schools in session storage. Just return that
            // instead of hitting the server again.
            // Use the of() wrapper to turn the session storage data into an observable.
            return of<SchoolInfo[]>(self._AllSchools).pipe(take(1));
        } else {
            // No, it has not been loaded yet or is lost. Fetch it from the server again.
            return this.http.get<SchoolInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/GetAllSchools`).pipe(
                map(data => {
                    // Store what we were just sent in session storage.
                    self._AllSchools = data;

                    return data;
                })
            );
        }
    }

    // #endregion

    // #region Get All School Years Service Call

    GetAllSchoolYears() {
        const self = this;

        return self._AllSchoolYears
            ? of<string[]>(self._AllSchoolYears).pipe(take(1))
            : self.http.get<string[]>(self.configService.hostAddress + `Api/Services/IRIS/GetAllFiscalYears`).pipe(
                  map(years => {
                      self._AllSchoolYears = years;

                      return years;
                  })
              );
    }

    // #endregion

    // #region Get All Subordinates Service Call

    GetAllSubordinates(AuthToken: string, CurrentRole: string, SAPID: string) {
        const self = this;

        return self._AllSubordinatesSupervisorSAPID === SAPID && self._AllSubordinates && self._AllSubordinates.length
            ? of<PersonInfo[]>(self._AllSubordinates)
            : self.http
                  .get<PersonInfo[]>(
                      self.configService.hostAddress +
                          `Api/Services/IRIS/GetAllSubordinates` +
                          `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}`
                  )
                  .pipe(
                      map(data => {
                          self._AllSubordinatesSupervisorSAPID = SAPID;
                          self._AllSubordinates = data;

                          return data;
                      })
                  );
    }

    // #endregion

    // #region Get All Supervisors Service Call

    GetAllSupervisors(AuthToken: string) {
        const self = this;

        return self._AllSupervisors && !!self._AllSupervisors.length
            ? of<PersonInfo[]>(self._AllSupervisors).pipe(take(1))
            : this.http
                  .get<PersonInfo[]>(
                      this.configService.hostAddress + `Api/Services/IRIS/GetAllSupervisors?AuthToken=${AuthToken || ''}`
                  )
                  .pipe(
                      map(users => {
                          if (users && !!users.length) {
                              users.forEach(user => {
                                  user.LastName = user.EmployeeLastName;
                                  user.FirstName = user.EmployeeFirstName;
                                  user.Location = user.OrgUnitName;
                                  user.SAPID = user.EmployeeSAPID;
                              });
                          }

                          self._AllSupervisors = users;

                          return users;
                      })
                  );
    }

    // #endregion

    // #region Get Current School Year Service Call

    GetCurrentSchoolYear() {
        return this._CurrentSchoolYear
            ? of<string>(this._CurrentSchoolYear).pipe(take(1))
            : this.http.get<string>(this.configService.hostAddress + `Api/Services/IRIS/GetCurrentSchoolYear`).pipe(
                  map(year => {
                      this._CurrentSchoolYear = year;

                      return year;
                  })
              );
    }

    // #endregion

    // #region Get Dashboard Summary Stats Service Call

    GetDashboardSummaryStats(AuthToken: string, CurrentRole: string) {
        return this.http.get<DashboardData>(
            this.configService.hostAddress +
                `Api/Services/IRIS/GetDashboardSummaryStats` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}`
        );
    }

    // #endregion

    // #region Get Due Date "Service" Call

    GetDueDate(addedDays: number) {
        const self = this;

        const today = new Date();
        let dueDate = startOfDay(today);

        if (addedDays === 0) {
            return dueDate;
        }

        // Use the loaded work day calendar to look up the work day for today's date (if possible), then advance by the number of days
        // indicated and look up that date as well.
        // TODO: Do this more efficiently (binary search on date?)
        let tgtDueDate = null;

        if (self._WorkdayCalendars && !!self._WorkdayCalendars.length) {
            // First, try to retrieve the entry matching today's date.
            let dbtoday = self._WorkdayCalendars.find(w => isSameDay(w.CalendarDate, today));

            if (dbtoday != null) {
                // We found today's date. Now, if today's date is a workday -AND- it's currently 4pm or later, we will need
                // to advance to the next business day before applying any priority due date adjustments.
                // We also should do this if today's date is a weekend or known holiday day as well.
                if ((today.getHours() >= 16 && dbtoday.WorkDay) || isWeekend(today) || dbtoday.WorkDay !== true) {
                    // The current time is after 4pm on a work day, or is a weekend day, or is a non-workday. Update the dbtoday
                    // to be the next possible workday (if known)
                    dbtoday = self.GetNextWorkDay(dbtoday);
                }

                // Now, using the FY work day number, advance that by the requested number of days.
                const targetFYDayNum = dbtoday.FYWorkDayNum + addedDays;

                // And try to look up the matching calendar date for that figure.
                let dbdue = self._WorkdayCalendars.find(w => w.FYWorkDayNum === targetFYDayNum && w.FiscalYear === dbtoday.FiscalYear);

                if (dbdue != null) {
                    // We have the target date
                    tgtDueDate = dbdue.CalendarDate;
                } else {
                    // We were unable to find the target date based upon the FY work day number. That may not be an
                    // error, though. If we are near the end of the FY (such as June 29), the target date may lie
                    // "outside" of the current FY into the next. We will substract the maximum FY work day number
                    // from the target, then try again in the other fiscal year.
                    const maxFYDayNum = self._WorkdayCalendars
                        .filter(w => w.FiscalYear === dbtoday.FiscalYear && w.FYWorkDayNum != null)
                        .sort((a, b) => b.FYWorkDayNum - a.FYWorkDayNum)[0].FYWorkDayNum;

                    const nextFYDayNum = targetFYDayNum - maxFYDayNum;
                    const nextFY = self.GetNextFiscalYear(dbtoday.FiscalYear);

                    // Try again.
                    dbdue = self._WorkdayCalendars.find(w => w.FYWorkDayNum === nextFYDayNum && w.FiscalYear === nextFY);

                    if (dbdue != null) {
                        tgtDueDate = dbdue.CalendarDate;
                    }
                }
            }
        }

        // If we were unable to find the due date using the "official" calendars, just fallback to a regular non-weekend date system.
        if (tgtDueDate == null) {
            // Add the days, being sure to only use weekdays (M-F) for now, until we have a better way of doing this.
            for (let i = 1; i <= addedDays; ) {
                dueDate = addDays(dueDate, 1);
                if (!isWeekend(dueDate)) {
                    ++i;
                } // Only advance our counter if the date we just came to is not a weekend.
            }

            return dueDate;
        } else {
            return tgtDueDate;
        }
    }

    // #endregion

    // #region Get Email Preferences Service Call

    GetEmailPreferences(AuthToken: string, CurrentRole: string) {
        return this.http.get<EmailPreference[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/GetEmailPreferences?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}`
        );
    }

    // #endregion

    // #region Get Email Preference Options Service Call

    GetEmailPreferenceOptions() {
        return this._AllEmailPreferenceOptions && !!this._AllEmailPreferenceOptions.length
            ? of<EmailPreferenceOption[]>(this._AllEmailPreferenceOptions)
            : this.http
                  .get<EmailPreferenceOption[]>(this.configService.hostAddress + `Api/Services/IRIS/GetEmailPreferenceOptions`)
                  .pipe(
                      map(opts => {
                          this._AllEmailPreferenceOptions = opts;

                          return opts;
                      })
                  );
    }

    // #endregion

    // #region Get File Attachment List Service Call

    GetFileAttachmentList(AuthToken: string, RequestID: number) {
        return this.http
            .get<FileAttachmentInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/GetFileAttachmentList?AuthToken=${AuthToken || ''}&RequestID=${RequestID || ''}`
            )
            .pipe(
                map(data => {
                    if (data && !!data.length) {
                        data.forEach(d => {
                            d.DateUploaded = d.DateUploaded ? new Date(d.DateUploaded) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Next Fiscal Year

    GetNextFiscalYear(year: string) {
        return year.substring(0, 2) + (+year.substring(2, 4) + 1) + year.substring(4, 6) + (+year.substring(6) + 1);
    }

    // #endregion

    // #region Get Next Work Day

    GetNextWorkDay(today: WorkdayCalendarInfo) {
        const self = this;

        if (!today) {
            return today;
        }

        // Premise here is basic. If we have loaded workday calendars, try to
        // get the one whose work day # is one more than the one we were given.
        // If we don't have the calendars, just roll to the next non-weekend day.
        if (self._WorkdayCalendars && self._WorkdayCalendars.length) {
            const tgtDay = today.FYWorkDayNum + 1;

            let dbnext = self._WorkdayCalendars.find(w => w.FYWorkDayNum === tgtDay && w.FiscalYear === today.FiscalYear);

            if (dbnext != null) {
                // We have the target. Return it.
                return dbnext;
            } else {
                // Unable to find a target date based upon the FY work day number. This
                // can happen near the end of the FY (such as June 29), and so the target
                // date may lie "outside" of the current FY into the next. We will subtract
                // the maximum FY work day number from the target, then try again in the other
                // fiscal year.
                const maxFYDayNum = self._WorkdayCalendars
                    .filter(w => w.FiscalYear === today.FiscalYear && w.FYWorkDayNum != null)
                    .sort((a, b) => b.FYWorkDayNum - a.FYWorkDayNum)[0].FYWorkDayNum;

                const nextFYDayNum = tgtDay - maxFYDayNum;
                const nextFY = self.GetNextFiscalYear(today.FiscalYear);

                dbnext = self._WorkdayCalendars.find(w => w.FYWorkDayNum === nextFYDayNum && w.FiscalYear === nextFY);

                if (dbnext != null) {
                    return dbnext;
                } else {
                    return today;
                }
            }
        } else {
            return today;
        }
    }

    // #endregion

    // #region Get Out Of Office Settings By Cabinet SAPID Service Call

    GetOutOfOfficeSettingsByCabinetSAPID(AuthToken: string, CurrentRole: string, CabinetSAPID: string) {
        return this.http
            .get<OutOfOfficeSetting[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/GetOutOfOfficeSettingsByCabinetSAPID?AuthToken=${AuthToken || ''}` +
                    `&CurrentRole=${CurrentRole || ''}&CabinetSAPID=${CabinetSAPID || ''}`
            )
            .pipe(
                map(data => {
                    if (data && data.length) {
                        data.forEach(d => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Out Of Office Setting By ID Service Call

    GetOutOfOfficeSettingByID(AuthToken: string, CurrentRole: string, OutOfOfficeSettingID: number) {
        return this.http
            .get<OutOfOfficeSetting>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/GetOutOfOfficeSettingByID?AuthToken=${AuthToken || ''}` +
                    `&CurrentRole=${CurrentRole || ''}&OutOfOfficeSettingID=${OutOfOfficeSettingID || ''}`
            )
            .pipe(
                map(data => {
                    data.DateBegin = data.DateBegin ? new Date(data.DateBegin) : null;
                    data.DateEnd = data.DateEnd ? new Date(data.DateEnd) : null;

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Referral Job Time Service Call

    GetReferralJobTime(AuthToken: string, RequestID: number) {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/IRIS/GetReferralJobTime?AuthToken=${AuthToken || ''}&RequestID=${RequestID || ''}`
        );
    }

    // #endregion

    // #region Get Related Request Service Call

    GetRelatedRequest(AuthToken: string, CurrentRole: string, RelatedRequestLinkYear: string, RelatedRequestLinkNumber: string) {
        return this.http.get<number>(
            this.configService.hostAddress +
                `Api/Services/IRIS/GetRelatedRequest?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&RelatedRequestLinkYear=${RelatedRequestLinkYear || ''}&RelatedRequestLinkNumber=${RelatedRequestLinkNumber ||
                    ''}`
        );
    }

    // #endregion

    // #region Get Request Service Call

    GetRequest(AuthToken: string, CurrentRole: string, RequestID: number, WithReferral?: boolean) {
        const self = this;

        return this.http
            .get<RequestInfo>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/GetRequest?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                        ''}&IRISRequestID=${RequestID || ''}` +
                    `&WithReferral=${WithReferral || ''}`
            )
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Get Role List Service Call

    GetRoleList() {
        return this._AllRoles && !!this._AllRoles.length
            ? of<RoleInfo[]>(this._AllRoles)
            : this.http.get<RoleInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/GetRoleList`).pipe(
                  map(data => {
                      this._AllRoles = data;

                      return data;
                  })
              );
    }

    // #endregion

    // #region Get Superintendent Summary Stats Service Call

    GetSuperintendentSummaryStats(AuthToken: string, CurrentRole: string) {
        return this.http.get<SummaryStatistics>(
            this.configService.hostAddress +
                `Api/Services/IRIS/GetSuperintendentSummaryStats?AuthToke=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}`
        );
    }

    // #endregion

    // #region Get Workday Calendar Service Call

    GetWorkdayCalendar(IncludeNextYear?: boolean) {
        return this._WorkdayCalendars && !!this._WorkdayCalendars.length
            ? of<WorkdayCalendarInfo[]>(this._WorkdayCalendars)
            : this.http
                  .get<WorkdayCalendarInfo[]>(
                      this.configService.hostAddress +
                          `Api/Services/IRIS/GetWorkdayCalendar` +
                          `?IncludeNextYear=${IncludeNextYear != null ? IncludeNextYear : ''}`
                  )
                  .pipe(
                      map(days => {
                          if (days && !!days.length) {
                              days.forEach(d => {
                                  d.CalendarDate = d.CalendarDate ? new Date(d.CalendarDate) : null;
                              });
                          }

                          this._WorkdayCalendars = days;

                          return days;
                      })
                  );
    }

    // #endregion

    // #region Is Valid Workday "Service" Call

    IsValidWorkday(d?: Date) {
        if (!d) {
            return false;
        }

        const day = d.getDay();

        if (!this._WorkdayCalendars || !this._WorkdayCalendars.length) {
            // Without a workday calendar loaded, just assume anything is a workday if it is not on a weekend.
            return day !== 0 && day !== 6;
        }

        // Look up the provided date in the workdays calendars.
        const dbday = this._WorkdayCalendars.find(w => isSameDay(d, w.CalendarDate));

        if (!dbday) {
            // Cannot tell. Default to checking weekend day.
            return day !== 0 && day !== 6;
        } else {
            return dbday.WorkDay;
        }
    }

    // #endregion

    // #region Mark Board Time Service Call

    MarkBoardTime(AuthToken: string, CurrentRole: string, BoardInfo: ReferralUpdateInfo) {
        const self = this;

        return self.http
            .post<RequestInfo>(self.configService.hostAddress + `Api/Services/IRIS/MarkBoardTime`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                infoObject: {
                    ReferralID: (BoardInfo && BoardInfo.ReferralID) || '',
                    ResponderNotes: (BoardInfo && BoardInfo.ResponderNotes) || '',
                    TimeAction: (BoardInfo && BoardInfo.TimeAction) || '',
                    Priority: (BoardInfo && BoardInfo.Priority) || '',
                    DueDate: (BoardInfo && BoardInfo.DueDate) || '',
                },
            })
            .pipe(
                map(data => {
                    self.FormatRequestInfo(data, CurrentRole);

                    return data;
                })
            );
    }

    // #endregion

    // #region Perform Search Service Call

    PerformSearch(AuthToken: string, CurrentRole: string, SearchParams: SearchParameters) {
        const self = this;

        return this.http
            .post<RequestInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/PerformSearch`, {
                AuthToken,
                CurrentRole,
                SearchYear: SearchParams.srchYear,
                SearchIssueNumber: SearchParams.srchIssueNum,
                SearchRequestedBy: SearchParams.srchRequestedBy,
                SearchType: SearchParams.srchType,
                SearchDateBegin: SearchParams.srchDateBegin,
                SearchDateEnd: SearchParams.srchDateEnd,
                SearchSubject: SearchParams.srchSubject,
                SearchSchool: SearchParams.srchSchool,
                SearchReferredTo: SearchParams.srchReferredTo,
                SearchReferredBy: SearchParams.srchReferredBy,
                SearchConstituent: SearchParams.srchSource,
                SearchCategoryID: SearchParams.srchCategoryID,
                SearchDistrict: SearchParams.srchDistrict,
                SearchPriority: SearchParams.srchPriority,
                SearchUnassignedOnly: SearchParams.srchUnassignedOnly,
                SearchNeedingAction: SearchParams.srchNeedingAction,
            })
            .pipe(
                map(results => {
                    if (results && !!results.length) {
                        results.forEach(result => {
                            self.FormatRequestInfo(result, CurrentRole);
                        });
                    }

                    return results;
                })
            );
    }

    // #endregion

    // #region Reactivate Out Of Office Setting Service Call

    ReactivateOutOfOfficeSetting(AuthToken: string, CurrentRole: string, OutOfOfficeSettingID: number) {
        return this.http
            .get<OutOfOfficeSetting[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/ReactivateOutOfOfficeSetting?AuthToken=${AuthToken || ''}` +
                    `&CurrentRole=${CurrentRole || ''}&OutOfOfficeSettingID=${OutOfOfficeSettingID || ''}`
            )
            .pipe(
                map(data => {
                    if (data && data.length) {
                        data.forEach(d => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Recall Request Service Call

    RecallRequest(AuthToken: string, CurrentRole: string, RequestID: number) {
        const self = this;

        return this.http.get<number>(
            this.configService.hostAddress +
                `Api/Services/IRIS/RecallRequest?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&RequestID=${RequestID ||
                    ''}`
        );
    }

    // #endregion

    // #region Redirect Request Service Call

    RedirectRequest(AuthToken: string, CurrentRole: string, RedirectInformation: RedirectInfo) {
        const self = this;

        return this.http.post<number>(this.configService.hostAddress + `Api/Services/IRIS/RedirectRequest`, {
            AuthToken,
            CurrentRole,
            infoObject: RedirectInformation,
        });
    }

    // #endregion

    // #region Referral Forward To Recipient Service Call

    ReferralForwardToRecipient(AuthToken: string, CurrentRole: string, ForwardInfo: ReferralUpdateInfo) {
        const self = this;

        return this.http
            .post<RequestInfo>(this.configService.hostAddress + `Api/Services/IRIS/ReferralForwardToRecipient`, {
                AuthToken,
                CurrentRole,
                infoObject: ForwardInfo,
            })
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Referral Mark Unavailable Service Call

    ReferralMarkUnavailable(AuthToken: string, CurrentRole: string, UnavailInfo: ReferralUpdateInfo) {
        const self = this;

        return this.http
            .post<RequestInfo>(this.configService.hostAddress + `Api/Services/IRIS/ReferralMarkUnavailable`, {
                AuthToken,
                CurrentRole,
                infoObject: UnavailInfo,
            })
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Referral Request Time Service Call

    ReferralRequestTime(AuthToken: string, CurrentRole: string, TimeInfo: ReferralUpdateInfo) {
        const self = this;

        return this.http
            .post<RequestInfo>(this.configService.hostAddress + `Api/Services/IRIS/ReferralRequestTime`, {
                AuthToken,
                CurrentRole,
                infoObject: TimeInfo,
            })
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Remove Board Assistant Service Call

    RemoveBoardAssistant(AuthToken: string, CurrentRole: string, BoardAssistantID: number) {
        return this.http.get<PersonInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/RemoveBoardAssistant` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&BoardAssistantID=${BoardAssistantID || ''}`
        );
    }

    // #endregion

    // #region Remove Cabinet Member Service Call

    RemoveCabinetMember(AuthToken: string, CurrentRole: string, SAPID: string) {
        return this.http
            .get<PersonInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/RemoveCabinetMember` +
                    `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&SAPID=${SAPID || ''}`
            )
            .pipe(
                map(cabinetmembers => {
                    this._AllCabinetMembers = cabinetmembers || [];

                    return this._AllCabinetMembers;
                })
            );
    }

    // #endregion

    // #region Remove Category Service Call

    RemoveCategory(AuthToken: string, CurrentRole: string, CategoryID: number) {
        return this.http.get<CategoryInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/RemoveCategory` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&CategoryID=${CategoryID || ''}`
        );
    }

    // #endregion

    // #region Remove Facilitator Service Call

    RemoveFacilitator(AuthToken: string, CurrentRole: string, FacilitatorID: number) {
        return this.http.get<PersonInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/RemoveFacilitator` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&FacilitatorID=${FacilitatorID || ''}`
        );
    }

    // #endregion

    // #region Request Cancel Service Call

    RequestCancel(AuthToken: string, CurrentRole: string, RequestID: number) {
        const self = this;

        return this.http
            .get<RequestInfo>(
                this.configService.hostAddress +
                    `Api/Services/IRIS/RequestCancel?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                        ''}&RequestID=${RequestID || ''}`
            )
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Search For Employee By SAPID Service Call

    SearchForEmployeeBySAPID(AuthToken: string, SAPID: string) {
        return this.http.get<EmployeeInfo[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/SearchForEmployeeBySAPID?AuthToken=${AuthToken || ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Search For User Service Call

    SearchForUser(AuthToken: string, CurrentRole: string, FirstName?: string, LastName?: string, SAPID?: string) {
        return this.http.post<PersonInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/SearchForUser`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            FirstName: FirstName || '',
            LastName: LastName || '',
            SAPID: SAPID || '',
        });
    }

    // #endregion

    // #region Set Email Preference Service Call

    SetEmailPreference(
        AuthToken: string,
        CurrentRole: string,
        EmailPreferenceID: number | null,
        EmailPreferenceOptionID: number,
        RoleID: number,
        NonOwner: boolean,
        InstantEmail: boolean,
        DailyRoundupEmail: boolean
    ) {
        return this.http.get<EmailPreference[]>(
            this.configService.hostAddress +
                `Api/Services/IRIS/SetEmailPreference` +
                `?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&EmailPreferenceID=${EmailPreferenceID || ''}` +
                `&EmailPreferenceOptionID=${EmailPreferenceOptionID || ''}&RoleID=${RoleID || ''}&NonOwner=${
                    NonOwner === true ? 'true' : 'false'
                }` +
                `&InstantEmail=${InstantEmail === true ? 'true' : 'false'}&DailyRoundupEmail=${
                    DailyRoundupEmail === true ? 'true' : 'false'
                }`
        );
    }

    // #endregion

    // #region Toggle Theme Service Call

    ToggleTheme() {
        this.ThemeSubject.next(!this.ThemeSubject.value);
    }

    // #endregion

    // #region Update Referral Service Call

    UpdateReferral(AuthToken: string, CurrentRole: string, ReferralDetails: ReferralOptions) {
        return this.http.post<number>(this.configService.hostAddress + `Api/Services/IRIS/UpdateReferral`, {
            AuthToken,
            CurrentRole,
            infoObject: {
                IRISRequestId: ReferralDetails.RequestID,
                IRISReferralId: ReferralDetails.ReferralID,
                ReferralStatus: ReferralDetails.ReferralStatus,
                EstimatedJobTime: ReferralDetails.ReferralEstimatedJobTime,
                ActualJobTime: ReferralDetails.ReferralActualJobTime,
                StatusNote: ReferralDetails.ReferralNote,
                RequestResponse: ReferralDetails.RequestResponse,
                ReferrerNotes: ReferralDetails.ReferralReferrerNotes,
                ReferralResponse: ReferralDetails.ReferralResponse,
            },
        });
    }

    // #endregion

    // #region Update Request Service Call

    UpdateRequest(AuthToken: string, CurrentRole: string, RequestDetails: CreateOptions) {
        const self = this;

        return this.http
            .post<RequestInfo>(this.configService.hostAddress + `Api/Services/IRIS/UpdateRequest`, {
                AuthToken,
                infoObject: {
                    IRISRequestID: RequestDetails.RequestID,
                    RequestText: RequestDetails.RequestText,
                    Status: RequestDetails.Status,
                    RequestCloseNote: RequestDetails.RequestCloseNote,
                    // RequestSource: RequestDetails.RequestSource,
                    // RequestSourceDescription: RequestDetails.RequestSourceDescription,
                    Subject: RequestDetails.Subject,
                    SchoolNumber: RequestDetails.SchoolNumber,
                    CategoryID: RequestDetails.RequestCategory.CategoryID,
                    BoardDistrict: RequestDetails.BoardDistrict,
                    Priority: RequestDetails.Priority,
                    RequestResponse: RequestDetails.RequestResponse,
                    ReferralTarget: RequestDetails.ResponderInfo.SAPID,
                    ReferralDeadline: RequestDetails.DueDate,
                    ReferralMessage: RequestDetails.RequestReferredMessage,
                },
            })
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Update Request Status Service Call

    UpdateRequestStatus(AuthToken: string, CurrentRole: string, RequestDetails: CreateOptions) {
        const self = this;

        return this.http
            .post<RequestInfo>(self.configService.hostAddress + `Api/Services/IRIS/UpdateRequestStatus`, {
                AuthToken,
                infoObject: {
                    RequestID: RequestDetails.RequestID,
                    Status: RequestDetails.Status,
                    RequestCloseNote: RequestDetails.RequestCloseNote,
                    SourceNotes: RequestDetails.SourceNotes,
                },
            })
            .pipe(
                map(request => {
                    self.FormatRequestInfo(request, CurrentRole);

                    return request;
                })
            );
    }

    // #endregion

    // #region Upsert Categories Service Call

    UpsertCategories(AuthToken: string, CurrentRole: string, Updates: UpsertCategoryInfo[]) {
        return this.http.post<CategoryInfo[]>(this.configService.hostAddress + `Api/Services/IRIS/UpsertCategories`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            Categories: Updates || '',
        });
    }

    // #endregion

    // #region Upsert Out Of Office Setting Service Call

    UpsertOutOfOfficeSetting(
        AuthToken: string,
        CurrentRole: string,
        OutOfOfficeSettingID: number,
        CabinetSAPID: string,
        DesigneeSAPID: string,
        DateBegin: Date,
        DateEnd: Date,
        Active: boolean,
        Categories: number[]
    ) {
        return this.http
            .post<OutOfOfficeSetting[]>(this.configService.hostAddress + `Api/Services/IRIS/UpsertOutOfOfficeSetting`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                OutOfOfficeSettingID: OutOfOfficeSettingID != null ? OutOfOfficeSettingID : '',
                CabinetSAPID: CabinetSAPID || '',
                DesigneeSAPID: DesigneeSAPID || '',
                DateBegin: DateBegin != null ? DateBegin.toJSON() : '',
                DateEnd: DateEnd != null ? DateEnd.toJSON() : '',
                Active: Active != null ? Active : '',
                Categories: Categories || '',
            })
            .pipe(
                map(data => {
                    if (data && data.length) {
                        data.forEach(d => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion
}

// #region Custom Classes

// #region Add Request Class

export class AddRequest {
    RequestID?: number;
    RequestIssueNumber?: number;
}

// #endregion

// #region Board Member Info Class

export class BoardMemberInfo {
    FirstName: string;
    LastName: string;
    SAPID: string;
    District: string;
    Current: boolean;
}

// #endregion

// #region Category Info Class

export class CategoryInfo {
    CategoryID?: number;
    CategoryDescription?: string;
    CabinetMember?: PersonInfo;
    SortOrder?: number;
    InUse?: boolean;

    // Client use
    Changed?: boolean;
    OriginalCategoryDescription?: string;
    OriginalCabinetMember?: PersonInfo;
}

// #endregion

// #region Create Options Class

export class CreateOptions {
    Subject: string;
    RequestCategory: CategoryInfo;
    RequestedBySAPID: string;
    DueDate: Date;
    Priority: number;
    Confidential?: boolean;
    OriginalPriority: number;
    Source: number;
    SourceDescription: string;
    RequestBoardMember: PersonInfo;
    SchoolNumber: string;
    BoardDistrict: string;
    RequestText: string;
    RequestIssueNumber?: number;
    RequestorInfo: PersonInfo;
    RequestorName: string;
    ReferrerInfo: PersonInfo;
    RequestReferralTarget: string;
    RequestReferredMessage: string;
    RequestID?: number;
    Status?: string;
    RequestCloseNote?: string;
    RequestResponse?: string;
    SourceNotes?: string;
    ResponderInfo?: PersonInfo;
    ResponderName?: string;
    SecondResponderInfo?: PersonInfo;
    ConstituentName?: string;
    ConstituentEmail?: string;
    ConstituentPhone?: string;
    ConstituentPrivate?: boolean;

    IsRelatedRequest?: boolean;
    RelatedRequestID?: number;

    RequestUploadAttachments?: FileUploadInformation[];
    RequestAttachments?: FileAttachmentInfo[];
    ResponseAttachments?: FileAttachmentInfo[];
    FollowUpAttachments?: FileAttachmentInfo[];
}

// #endregion

// #region Current Request Info Class

export class CurrentRequestInfo {
    RequestID?: number;
    IssueNumber?: number;
    FiscalYear?: string;
}

// #endregion

// #region Dashboard Data Class

export class DashboardData {
    RequestsSummary?: RequestSummaryStatistics[];
    BoardMembersSummary?: RequestSummaryStatistics[];
    CategoriesSummary?: RequestSummaryStatistics[];
}

// #endregion

// #region Email Preference Class

export class EmailPreference {
    EmailPreferenceID: number;
    EmailPreferenceOptionID: number;
    EventCode: string;
    RoleID: number;
    RoleName: string;
    NonOwner: boolean;
    InstantEmail: boolean;
    DailyRoundupEmail: boolean;

    // For UI
    InstantChanged: boolean;
    DailyChanged: boolean;
    MobileEmail: string;
}

// #endregion

// #region Email Preference Option Class

export class EmailPreferenceOption {
    EmailPreferenceOptionID: number;
    EventCode: string;
    Description: string;
}

// #endregion

// #region Employee Info Class

export class EmployeeInfo {
    SAPID: string;
    LastName: string;
    FirstName: string;
    LocalJobTitle: string;
    UserID?: number;
    WorkLocation: string;
    SiteName?: string;
    PositionNo: string;
}

// #endregion

// #region File Attachment Info Class

export class FileAttachmentInfo {
    FileName?: string;
    FileSize?: number;
    FileStorageURL?: string;
    FileStatus?: string;
    FileTypeCode?: string;
    FileTypeName?: string;
    FileAttachmentID?: number;
    IsDeleting?: boolean;
    IsInProgress?: boolean;
    NominationFormUID?: string;
    OriginalFileName?: string;
    SharepointURL?: string;
    Source?: string;
    SortOrder?: number;
    ReferralID?: number;
    Uploader?: number;
    UploaderSAPID?: string;
    UploaderName?: string;
    DateUploaded?: Date;
    UploadID?: number;
    UploadPercentComplete?: number;
    UploadStatus?: string;
}

// #endregion

// #region New Request Info Class

export class NewRequestInfo {
    RequestID?: number;
    RequestSubject?: string;
    RequestCategoryId?: number;
    RequestDueDate?: Date;
    RequestIssueNumber?: string;
    RequestPriority?: string;
    RequestSource?: string;
    RequestSourceDescription?: string;
    RequestSchool?: string;
    RequestDistrict?: string;
    RequestText?: string;
}

// #endregion

// #region Out Of Office Setting Class

export class OutOfOfficeSetting {
    OutOfOfficeSettingID: number;
    CabinetSAPID: string;
    FirstName: string;
    LastName: string;
    LocalJobTitle: string;
    DesigneeSAPID: string;
    DesigneeFirstName: string;
    DesigneeLastName: string;
    DesigneeLocalJobTitle: string;
    DateBegin: Date;
    DateEnd?: Date;
    Active: boolean;
    InWindow: boolean;
    Categories?: CategoryInfo[];
}

// #endregion

// #region Person Info Class

export class PersonInfo {
    PersonID: number;
    LastName: string;
    FirstName: string;
    FullName: string;
    Location: string;
    LocalJobTitle: string;
    PositionNo: string;
    SAPID: string;
    District: string;
    Type: string;
    Active: boolean;
    Email: string;

    // From server
    EmployeeLastName?: string;
    EmployeeFirstName?: string;
    OrgUnitName?: string;
    EmployeeSAPID?: string;
}

// #endregion

// #region Redirect Info Class

export class RedirectInfo {
    RequestID?: number;
    CategoryID?: number;
    Note?: string;
}

// #endregion

// #region Referral Info Class

export class ReferralInfo {
    ReferralID?: number;
    RequestID?: number;
    RequestDueDate?: Date;
    OwnerSAPID?: string;
    SourceSAPID?: string;
    OwnerInfo?: PersonInfo;
    SourceInfo?: PersonInfo;
    DateReferred?: Date;
    RequestStatus?: string;
    EstimatedJobTime?: number;
    ActualJobTime?: number;
    ReferralDateUpdated?: Date;
    RequestedBySAPID?: string;
    RequesterInfo?: PersonInfo;
    CommentsFromSource?: RequestCommentInfo[];
    CommentsFromResponder?: RequestCommentInfo[];
}

// #endregion

// #region Referral Options Class

export class ReferralOptions {
    RequestID?: number;
    ReferralID?: number;
    ReferralStatus?: string;
    ReferralNote?: string;
    ReferralEstimatedJobTime?: number;
    ReferralActualJobTime?: number;
    RequestResponse?: string;
    ReferralReferrerNotes?: string;
    ReferralResponse?: string;
}

// #endregion

// #region Referral Update Info Class

export class ReferralUpdateInfo {
    RequestID?: number;
    ReferralID?: number;
    StatusNote?: string;
    Response?: string;
    Confidential?: boolean;
    SourceNotes?: string;
    EstimatedJobTime?: number;
    ActualJobTime?: number;
    ResponderNotes?: string;
    RecipientSAPID?: string;
    TimeAction?: string;
    Priority?: number;
    DueDate?: Date;
}

// #endregion

// #region Request Comment Info Class

export class RequestCommentInfo {
    Comment?: string;
    DateCommentAdded?: Date;
    CommentID?: number;
    CommentRelationship?: string;
    CommentCreatorSAPID?: string;
    CommentCreatorFirstName?: string;
    CommentCreatorLastName?: string;
    CommentCreatorFullName?: string;
    CommentCreatorLocalJobTitle?: string;
    HasRelatedLink?: boolean;
    RelatedRequestLinkYear?: string;
    RelatedRequestLinkNumber?: string;
}

// #endregion

// #region Request FollowUp Info

export class RequestFollowUpInfo {
    FollowUpID?: number;
    RequestID?: number;
    FollowUpQuestion: string;
    DateRequested: Date | string;
    RequestedBySAPID: string;
    RequestedByFirstName: string;
    RequestedByLastName: string;
    RequestedByFullName: string;
    RequestedByLocalJobTitle: string;
    Response: string;
    DateResponded: Date | string | null;
    RespondedBySAPID: string;
    RespondedByFirstName: string;
    RespondedByLastName: string;
    RespondedByFullName: string;
    RespondedByLocalJobTitle: string;
    Attachments?: FileAttachmentInfo[];
}

// #endregion

// #region Request Info Class

export class RequestInfo extends CreateOptions {
    BoardPriority: number;
    CategoryID?: number;
    CreatedBySAPID?: string;
    DateCreated?: Date;
    StatusLabel?: string;
    DateUpdated?: Date;
    FiscalYear?: string;
    FormattedRequestIssueNumber?: string;
    RequestResponses?: RequestResponseInfo[];
    ReferralID?: number;
    CurrentReferral?: boolean;
    ReferralDirection?: string;
    OwnerSAPID?: string;
    SourceSAPID?: string;
    DateReferred?: Date;
    EstimatedJobTime?: number;
    ActualJobTime?: number;
    ReferralDateUpdated?: Date;
    RequestIsClosed?: boolean;
    CategoryLabel?: string;
    PriorityLabel?: string;
    PriorityShortLabel?: string;
    ReferralIsClosed?: boolean;
    ReferralFacilitatorStatus?: string;
    DateClosed?: Date;
    Completed?: boolean;
    Cancelled?: boolean;
    IsPastOwner?: boolean;
    AsDesignee?: boolean;
    Comments?: RequestCommentInfo[];
    FollowUpCount?: number;
    FollowUps?: RequestFollowUpInfo[];
}

// #endregion

// #region Request Response Info Class

export class RequestResponseInfo {
    Response?: string;
    DateAdded?: Date;
    ResponseID?: number;
    ReferralID?: number;
    ResponderSAPID?: string;
    ResponderFirstName?: string;
    ResponderLastName?: string;
    ResponderLocalJobTitle?: string;
    ResponderDesigneeSAPID?: string;
    ResponderDesigneeFirstName?: string;
    ResponderDesigneeLastName?: string;
    ResponderDesigneeLocalJobTitle?: string;
}

// #endregion

// #region Request Summary Statistics Class

export class RequestSummaryStatistics {
    RequestType?: string;
    FirstName?: string;
    LastName?: string;
    CategoryDescription?: string;
    RequestFiscalYear?: string;
    TotalCount?: number;
    TotalPercent?: number;
    CancelledCount?: number;
    CancelledPercent?: number;
    CancelledNoPriorityCount?: number;
    CancelledNoPriorityPercent?: number;
    ClosedNonInfoCount?: number;
    ClosedNonInfoPercent?: number;
    AllUnassigned?: number;
    AllUnassignedPercent?: number;
    AllUnanswered?: number;
    AllUnansweredPercent?: number;
    AllAnswered?: number;
    AllAnsweredPercent?: number;
    AllComplete?: number;
    AllCompletePercent?: number;
    AllJobTime?: number;
    UrgentCount?: number;
    UrgentPercent?: number;
    UrgentCancelledCount?: number;
    UrgentCancelledPercent?: number;
    UrgentPendingCount?: number;
    UrgentPendingPercent?: number;
    UrgentUnassigned?: number;
    UrgentUnassignedPercent?: number;
    UrgentUnanswered?: number;
    UrgentUnansweredPercent?: number;
    UrgentAnswered?: number;
    UrgentAnsweredPercent?: number;
    UrgentComplete?: number;
    UrgentCompletePercent?: number;
    UrgentJobTime?: number;
    HighCount?: number;
    HighPercent?: number;
    HighCancelledCount?: number;
    HighCancelledPercent?: number;
    HighPendingCount?: number;
    HighPendingPercent?: number;
    HighUnassigned?: number;
    HighUnassignedPercent?: number;
    HighUnanswered?: number;
    HighUnansweredPercent?: number;
    HighAnswered?: number;
    HighAnsweredPercent?: number;
    HighComplete?: number;
    HighCompletePercent?: number;
    HighJobTime?: number;
    RoutineCount?: number;
    RoutinePercent?: number;
    RoutineCancelledCount?: number;
    RoutineCancelledPercent?: number;
    RoutinePendingCount?: number;
    RoutinePendingPercent?: number;
    RoutineUnassigned?: number;
    RoutineUnassignedPercent?: number;
    RoutineUnanswered?: number;
    RoutineUnansweredPercent?: number;
    RoutineAnswered?: number;
    RoutineAnsweredPercent?: number;
    RoutineComplete?: number;
    RoutineCompletePercent?: number;
    RoutineJobTime?: number;
    LowCount?: number;
    LowPercent?: number;
    LowCancelledCount?: number;
    LowCancelledPercent?: number;
    LowPendingCount?: number;
    LowPendingPercent?: number;
    LowUnassigned?: number;
    LowUnassignedPercent?: number;
    LowUnanswered?: number;
    LowUnansweredPercent?: number;
    LowAnswered?: number;
    LowAnsweredPercent?: number;
    LowComplete?: number;
    LowCompletePercent?: number;
    LowJobTime?: number;
    PendingTimeCount?: number;
    PendingTimePercent?: number;
}

// #endregion

// #region Role Info Class

export class RoleInfo {
    RoleID?: number;
    RoleDescription?: string;
    RoleName?: string;
}

// #endregion

// #region School Info Class

export class SchoolInfo {
    SchoolYear?: string;
    SchoolNumber?: string;
    SchoolName?: string;
    SchoolPhoneNumber?: string;
    Principal?: string;
    Magnet?: boolean;
    Charter?: boolean;
    Area?: string;
    AreaDescription?: string;
    Enr_PK?: boolean;
    Enr_KG?: boolean;
    Enr_01?: boolean;
    Enr_02?: boolean;
    Enr_03?: boolean;
    Enr_04?: boolean;
    Enr_05?: boolean;
    Enr_06?: boolean;
    Enr_07?: boolean;
    Enr_08?: boolean;
    Enr_09?: boolean;
    Enr_10?: boolean;
    Enr_11?: boolean;
    Enr_12?: boolean;
    Enr_30?: boolean;
    Enr_31?: boolean;
    Uses_Elegrade?: boolean;
    ExcludeFromEnrCounts?: boolean;
    IsElementary?: boolean;
    IsMiddle?: boolean;
    IsSenior?: boolean;
    IsAdult?: boolean;
    DOE_School_Number?: string;
    CommunityEligibilityOption?: boolean;
    FiscalYear?: string;
    District?: string;
}

// #endregion

// #region Search Parameters Class

export class SearchParameters {
    srchYear?: string;
    srchIssueNum?: string;
    srchType?: number;
    srchDateBegin?: Date;
    srchDateEnd?: Date;
    srchSubject?: string;
    srchSchool?: string;
    srchReferredTo?: string;
    srchReferredBy?: string;
    srchSource?: string;
    srchBoardMember?: BoardMemberInfo;
    srchCategoryID?: string;
    srchCategory?: CategoryInfo;
    srchDistrict?: string;
    srchUnassignedOnly?: boolean;
    srchNeedingAction?: boolean;
    srchPriority?: number;
    srchRequestedBy?: string;
}

// #endregion

// #region Summary Statistics Class

export class SummaryStatistics {
    RequestSource?: string;
    RequestFiscalYear?: string;
    TotalCount?: number;
    UrgentCount?: number;
    UrgentUnassigned?: number;
    UrgentUnassignedPercent?: number;
    UrgentUnanswered?: number;
    UrgentUnansweredPercent?: number;
    UrgentAnswered?: number;
    UrgentAnsweredPercent?: number;
    UrgentComplete?: number;
    UrgentCompletePercent?: number;
    HighCount?: number;
    HighUnassigned?: number;
    HighUnassignedPercent?: number;
    HighUnanswered?: number;
    HighUnansweredPercent?: number;
    HighAnswered?: number;
    HighAnsweredPercent?: number;
    HighComplete?: number;
    HighCompletePercent?: number;
    RoutineCount?: number;
    RoutineUnassigned?: number;
    RoutineUnassignedPercent?: number;
    RoutineUnanswered?: number;
    RoutineUnansweredPercent?: number;
    RoutineAnswered?: number;
    RoutineAnsweredPercent?: number;
    RoutineComplete?: number;
    RoutineCompletePercent?: number;
    LowCount?: number;
    LowUnassigned?: number;
    LowUnassignedPercent?: number;
    LowUnanswered?: number;
    LowUnansweredPercent?: number;
    LowAnswered?: number;
    LowAnsweredPercent?: number;
    LowComplete?: number;
    LowCompletePercent?: number;
    AllUnassigned?: number;
    AllUnassignedPercent?: number;
    AllUnanswered?: number;
    AllUnansweredPercent?: number;
    AllAnswered?: number;
    AllAnsweredPercent?: number;
    AllComplete?: number;
    AllCompletePercent?: number;
}

// #endregion

// #region Upsert Category Info Class

export class UpsertCategoryInfo {
    IRISCategoryID?: number;
    CategoryDescription?: string;
    CabinetSAPID?: string;
    SortOrder?: number;
    ToRemove?: boolean;
    Changed?: boolean;
}

// #endregion

// #region User Search Result Class

export class UserSearchResult {
    FirstName?: string;
    LastName?: string;
    City?: string;
    Email?: string;
    ActiveDirectoryEmail?: string;
    UserID?: number;
    Username?: string;
    AsAdmin?: boolean;
    SAPID?: string;
    PositionNo?: string;
    LocalJobTitle?: string;
    Password?: string;
}

// #endregion

// #region Workday Calendar Info Class

export class WorkdayCalendarInfo {
    FiscalYear?: string;
    CalendarDate?: Date;
    WorkDay?: boolean;
    FYWorkDayNum?: number;
}

// #endregion

// #endregion

// #region Custom Enumerations

// #endregion
